import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import api from "@/lib/api";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Toaster } from "@/components/ui/sonner";

import Home from "@/pages/Home";
import CityDirectory from "@/pages/CityDirectory";
import CompanyDetail from "@/pages/CompanyDetail";
import QuoteForm from "@/pages/QuoteForm";
import Legal from "@/pages/Legal";
import Referencement from "@/pages/Referencement";
import CookieBanner from "@/components/CookieBanner";
import PartnersList from "@/pages/PartnersList";
import AllCities from "@/pages/AllCities";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminCompanies from "@/pages/AdminCompanies";
import AdminLeadDetail from "@/pages/AdminLeadDetail";
import AdminSirene from "@/pages/AdminSirene";
import DepartmentDirectory from "@/pages/DepartmentDirectory";
import CompanyCorrection from "@/pages/CompanyCorrection";
import AdminCorrections from "@/pages/AdminCorrections";
import SeoLanding from "@/pages/SeoLanding";
import AdminSeo from "@/pages/AdminSeo";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";
import IntercityLanding from "@/pages/IntercityLanding";
import { INTERCITY_SLUGS, LEGACY_PARIS_SLUGS } from "@/data/intercityRoutes";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    <main className="min-h-[60vh]">{children}</main>
    <Footer />
  </>
);

function App() {
  // Injection des meta tags de vérification (Google, Bing) au chargement
  useEffect(() => {
    api.get("/seo/config").then((r) => {
      const { google_site_verification, bing_verification } = r.data || {};
      const upsert = (name, content) => {
        if (!content) return;
        let el = document.querySelector(`meta[name="${name}"]`);
        if (!el) {
          el = document.createElement("meta");
          el.setAttribute("name", name);
          document.head.appendChild(el);
        }
        el.setAttribute("content", content);
      };
      upsert("google-site-verification", google_site_verification);
      upsert("msvalidate.01", bing_verification);
    }).catch(() => {});
  }, []);

  return (
    <div className="App" data-testid="app-root">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Routes>
            {/* Public */}
            <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
            <Route path="/villes" element={<PublicLayout><AllCities /></PublicLayout>} />
            <Route path="/demenageurs/departement/:code" element={<PublicLayout><DepartmentDirectory /></PublicLayout>} />
            <Route path="/demenageurs/:citySlug" element={<PublicLayout><CityDirectory /></PublicLayout>} />
            <Route path="/demenageur/:slug" element={<PublicLayout><CompanyDetail /></PublicLayout>} />
            <Route path="/devis" element={<PublicLayout><QuoteForm /></PublicLayout>} />
            <Route path="/mentions-legales" element={<PublicLayout><Legal pageKey="mentions-legales" /></PublicLayout>} />
            <Route path="/cgu" element={<PublicLayout><Legal pageKey="cgu" /></PublicLayout>} />
            <Route path="/confidentialite" element={<PublicLayout><Legal pageKey="confidentialite" /></PublicLayout>} />
            <Route path="/cookies" element={<PublicLayout><Legal pageKey="cookies" /></PublicLayout>} />
            <Route path="/referencement" element={<PublicLayout><Referencement /></PublicLayout>} />
            <Route path="/partenaires" element={<PublicLayout><PartnersList /></PublicLayout>} />
            <Route path="/a-propos" element={<PublicLayout><Legal pageKey="a-propos" /></PublicLayout>} />
            <Route path="/mentions-legales-b2b" element={<PublicLayout><Legal pageKey="mentions-legales-b2b" /></PublicLayout>} />
            <Route path="/fiche-entreprise/modifier" element={<PublicLayout><CompanyCorrection /></PublicLayout>} />

            {/* SEO Landing pages */}
            <Route path="/demenagement/:regionSlug" element={<PublicLayout><SeoLanding /></PublicLayout>} />
            <Route path="/demenagement/:regionSlug/:deptSlugNumero" element={<PublicLayout><SeoLanding /></PublicLayout>} />
            <Route path="/demenagement/:regionSlug/:deptSlugNumero/:citySlug" element={<PublicLayout><SeoLanding /></PublicLayout>} />

            {/* Blog */}
            <Route path="/blog" element={<PublicLayout><BlogList /></PublicLayout>} />
            <Route path="/blog/:slug" element={<PublicLayout><BlogPost /></PublicLayout>} />

            {/* Pages inter-villes SEO — nouveau format /demenagement-{origin}-vers-{destination} (96 trajets) */}
            {INTERCITY_SLUGS.map((slug) => (
              <Route
                key={slug}
                path={slug}
                element={<PublicLayout><IntercityLanding /></PublicLayout>}
              />
            ))}

            {/* Rétrocompatibilité — 15 anciennes URLs Paris → X déjà indexées */}
            {LEGACY_PARIS_SLUGS.map((slug) => (
              <Route
                key={`legacy-${slug}`}
                path={slug}
                element={<PublicLayout><IntercityLanding /></PublicLayout>}
              />
            ))}

            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/companies" element={<ProtectedRoute><AdminCompanies /></ProtectedRoute>} />
            <Route path="/admin/sirene" element={<ProtectedRoute><AdminSirene /></ProtectedRoute>} />
            <Route path="/admin/leads/:id" element={<ProtectedRoute><AdminLeadDetail /></ProtectedRoute>} />
            <Route path="/admin/corrections" element={<ProtectedRoute><AdminCorrections /></ProtectedRoute>} />
            <Route path="/admin/seo" element={<ProtectedRoute><AdminSeo /></ProtectedRoute>} />

            {/* 404 fallback */}
            <Route path="*" element={<PublicLayout><div className="container mx-auto px-6 py-24 text-center"><h1 className="font-heading font-black text-5xl mb-4">404</h1><p className="text-[#5C5C5C]">Page introuvable.</p></div></PublicLayout>} />
          </Routes>
          <CookieBanner />
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
